/**
 * Whether first-run setup has been answered on this browser.
 *
 * The gate in `App.tsx` reads the server: nothing logged *and* no budget means
 * nobody has set up. Both of those stay true for somebody who pressed "Skip —
 * just count calories" and has not eaten yet, so the answer has to be recorded
 * somewhere or the form comes back on the next load and "skip" is only an exit
 * until you refresh. `FirstRun`'s docstring already promises it is a real exit;
 * this is what makes that true across a reload.
 *
 * Here rather than the profile, which would be the tidier home for it. The
 * skip is an answer about a screen, and the server has no field for "asked and
 * declined" — adding one means a migration and an endpoint for a boolean that
 * only the web shell ever reads. `localStorage` rather than `sessionStorage`
 * because an answer that evaporates with the tab is the form-every-visit this
 * exists to end, and synchronously because the gate is decided during a render.
 *
 * Signing out clears it (see `session.tsx`): the next person to use this
 * browser must not inherit the last one's "I skipped". A session that merely
 * expired does not, because that is the same person coming back.
 *
 * Every access is wrapped. Storage throws rather than returning null in Safari
 * with site data blocked, and a private window is not a reason for the app to
 * be a white page. A failed write costs this browser the answer on the next
 * load — the setup form once more — which is exactly where this started.
 */

const KEY = "calorio:setup-done";

/** Has setup been answered here, by finishing it or by skipping it? */
export function setupDone(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

/** Remember that setup has been answered on this browser. */
export function markSetupDone(): void {
  try {
    localStorage.setItem(KEY, "1");
  } catch {
    // Setup still stays gone for this visit; it just returns on the next load,
    // which is the behaviour that existed before this file.
  }
}

/** Forget the answer — for a browser being handed to the next account. */
export function clearSetupDone(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to undo: if the read throws too, the stored answer is unreachable
    // anyway and the gate falls back to asking.
  }
}
